// ./src/components/features/Forms/FormFieldsEditModal/FileFieldOptionsEditor.tsx
import React from "react";
import {
  Stack,
  NumberInput,
  TagsInput,
  Text,
  Group,
  Badge,
} from "@mantine/core";
import { FileFieldOptions } from "./fieldOptionsTypes";

export interface FileFieldOptionsEditorProps {
  value: FileFieldOptions;
  onChange: (options: FileFieldOptions) => void;
}

// Common file extension presets
const EXTENSION_PRESETS: Record<
  string,
  { label: string; extensions: string[] }
> = {
  documents: {
    label: "Documents",
    extensions: ["pdf", "doc", "docx", "txt", "rtf"],
  },
  images: {
    label: "Images",
    extensions: ["jpg", "jpeg", "png", "gif", "webp", "svg"],
  },
  spreadsheets: { label: "Spreadsheets", extensions: ["xls", "xlsx", "csv"] },
};

export const FileFieldOptionsEditor: React.FC<FileFieldOptionsEditorProps> = ({
  value,
  onChange,
}) => {
  const handleChange = (field: keyof FileFieldOptions, fieldValue: any) => {
    onChange({
      ...value,
      [field]: fieldValue,
    });
  };

  const handleExtensionsChange = (extensions: string[]) => {
    // Clean up extensions: lowercase, remove dots and empty strings
    const cleanedExtensions = extensions
      .map((ext) => ext.trim().toLowerCase().replace(/^\.+/, ""))
      .filter((ext) => ext.length > 0);

    // Remove duplicates
    const uniqueExtensions = [...new Set(cleanedExtensions)];

    onChange({
      ...value,
      extensions: uniqueExtensions.length > 0 ? uniqueExtensions : undefined,
    });
  };

  const handlePresetClick = (presetKey: string) => {
    const preset = EXTENSION_PRESETS[presetKey];
    if (!preset) return;

    // Merge with existing extensions
    const currentExtensions = value.extensions || [];
    const newExtensions = [
      ...new Set([...currentExtensions, ...preset.extensions]),
    ];

    onChange({
      ...value,
      extensions: newExtensions,
    });
  };

  const handleClearExtensions = () => {
    onChange({
      ...value,
      extensions: undefined,
    });
  };

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Configure file upload validation.
      </Text>

      <NumberInput
        label="Maximum Files"
        placeholder="1"
        description="Maximum number of files allowed per submission"
        value={value.maxQuantity ?? ""}
        onChange={(val) =>
          handleChange("maxQuantity", val === "" ? undefined : val)
        }
        min={1}
        max={20}
        size="sm"
      />

      <TagsInput
        label="Allowed Extensions"
        placeholder="Type extension and press Enter or comma"
        description="Add file extensions without dots (e.g., pdf, jpg, docx)"
        value={value.extensions || []}
        onChange={handleExtensionsChange}
        splitChars={[",", " ", ";"]}
        clearable
        size="sm"
        maxTags={30}
      />

      <Group gap="xs">
        <Text size="xs" c="dimmed">
          Quick add:
        </Text>
        {Object.entries(EXTENSION_PRESETS).map(([key, preset]) => (
          <Badge
            key={key}
            variant="light"
            color="blue"
            size="sm"
            style={{ cursor: "pointer" }}
            onClick={() => handlePresetClick(key)}
          >
            + {preset.label}
          </Badge>
        ))}
        {value.extensions && value.extensions.length > 0 && (
          <Badge
            variant="light"
            color="red"
            size="sm"
            style={{ cursor: "pointer" }}
            onClick={handleClearExtensions}
          >
            Clear all
          </Badge>
        )}
      </Group>
    </Stack>
  );
};
